import React, { useContext, useEffect } from 'react';
import { useForm, FormProvider, UseFormSetError, FieldValues } from 'react-hook-form';
import classNames from 'classnames';

import useApi, { UseInvalidateEndpoint } from 'common/useApi';
import useFormApiErrors from 'common/useFormApiErrors';
import AuthContext, { ISubaccount } from 'common/auth';

import FormLabel from 'components/FormLabel';
import Loading from 'components/Loading';

type Props = { target: ISubaccount | null };

// Manual "pay out my balance now" — the time-based scheduler aside, a miner can
// trigger a payout on demand. OTP-gated (it moves real money) and delegates to
// the same backend executor as the scheduler. See docs/scheduled-payouts.md.
const PayoutNow = ({ target }: Props) => {
  const { user } = useContext(AuthContext)!;
  const invalidate = UseInvalidateEndpoint();
  const { isLoading, isFetching, isError, isSuccess, error, mutate } = useApi('post', '/api/me/payout-now');

  const formMethods = useForm({ defaultValues: { token: '' }, mode: 'onChange' });
  const {
    handleSubmit, register, reset, setValue,
    formState: { isSubmitting, isValid, isDirty },
    setError,
  } = formMethods;

  // Clear any entered OTP when the targeted subaccount changes.
  useEffect(() => {
    reset({ token: '' });
  }, [target?.id]);

  const balance = parseFloat(target?.balance ?? '0');
  const canPay = !!user?.has_device && !!target && balance > 0;
  const disabled = isSubmitting || isLoading || isFetching || !canPay || (!isValid && isDirty);

  const onSubmit = ({ token }: { token: string }) => {
    mutate({
      subaccount: target?.id,
      token,
      // Fresh per click so a network retry of this one submit collapses to a
      // single payout, while a later click (e.g. after a failure) is a new one.
      // (Just a dedup token — no need for crypto-grade randomness.)
      idempotency_key: `${target?.id ?? 'x'}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    });
  };

  useFormApiErrors(
    isError,
    error,
    setError as unknown as UseFormSetError<FieldValues>,
    isSuccess,
    'Payout sent',
  );

  useEffect(() => {
    if (isSuccess) {
      invalidate('/api/me');
      setValue('token', '');
    }
  }, [isSuccess]);

  return (
    <div
      className={classNames({
        box: true,
        table: true,
        isLoading: isLoading || isFetching || isSubmitting,
      })}
    >
      {(isLoading || isFetching || isSubmitting) && <Loading />}
      <h4>Pay Out Now</h4>
      <p className='payout-now-balance'>
        Available balance{target ? ` — ${target.username}` : ''}:{' '}
        <strong>{target?.balance ?? '0'} BTC</strong>
      </p>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormLabel
            field='token'
            name='Enter the one-time password generated by your OTP app'
            className='half'
          >
            <input
              type='text'
              placeholder='######'
              {...register('token', {
                minLength: { message: 'Token must be exactly 6 characters long', value: 6 },
                maxLength: { message: 'Token must be exactly 6 characters long', value: 6 },
                required: "Token can't be blank",
              })}
              disabled={!canPay}
            />
          </FormLabel>
          <button type='submit' className='primary big' disabled={disabled}>
            Pay out {target?.balance ?? '0'} BTC now
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

export default PayoutNow;
